import { useEffect, useMemo, useState } from "react";
import { PageTitle } from "../components/PageTitle";
import { Icon } from "../components/Icon";
import { useToast } from "../components/Toast";
import { Button, Field, Input, Select, Textarea } from "../components/ui";
import {
  usePdrSchema,
  type PdrRegistry,
  type RegistryField,
} from "../context/PdrSchemaContext";

type Row = {
  key: string;
  label: string;
  type: string;
  reasoned: boolean;
  required: boolean;
  enumValues: string;
  description: string;
  inSlim: boolean;
  inComplete: boolean;
  isNew: boolean;
};

function toRows(reg: PdrRegistry): Row[] {
  const slim = new Set(reg.profiles.slim ?? []);
  const complete = new Set(reg.profiles.complete ?? []);
  return reg.fields.map((f) => ({
    key: f.key,
    label: f.label,
    type: f.type,
    reasoned: f.reasoned,
    required: f.required ?? true,
    enumValues: (f.enum_values ?? []).join(", "),
    description: f.description ?? "",
    inSlim: slim.has(f.key),
    inComplete: complete.has(f.key),
    isNew: false,
  }));
}

function toPayload(rows: Row[]): { fields: RegistryField[]; profiles: Record<string, string[]> } {
  const fields: RegistryField[] = rows.map((r) => ({
    key: r.key.trim(),
    label: r.label.trim(),
    type: r.type,
    reasoned: r.reasoned,
    required: r.required,
    enum_values:
      r.type === "enum"
        ? r.enumValues.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
    description: r.description,
  }));
  return {
    fields,
    profiles: {
      slim: rows.filter((r) => r.inSlim).map((r) => r.key.trim()),
      complete: rows.filter((r) => r.inComplete).map((r) => r.key.trim()),
    },
  };
}

function Check({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-1.5 text-body-sm text-on-surface-variant cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-primary"
      />
      {label}
    </label>
  );
}

export function PdrSchemaPage() {
  const { registry, fieldTypes, versions, loading, error, save, rollback } = usePdrSchema();
  const { toast } = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");

  // Reset local edits whenever the registry changes (initial load / save / rollback).
  useEffect(() => {
    if (registry) setRows(toRows(registry));
  }, [registry]);

  const baseline = useMemo(
    () => (registry ? JSON.stringify(toPayload(toRows(registry))) : ""),
    [registry],
  );
  const dirty = useMemo(() => JSON.stringify(toPayload(rows)) !== baseline, [rows, baseline]);

  const types = fieldTypes.length ? fieldTypes : ["text", "long_text", "integer", "date", "enum", "list"];

  const patch = (idx: number, changes: Partial<Row>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...changes } : r)));

  const move = (idx: number, delta: number) =>
    setRows((prev) => {
      const next = [...prev];
      const j = idx + delta;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const remove = (idx: number) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const addField = () =>
    setRows((prev) => [
      ...prev,
      {
        key: "",
        label: "",
        type: "text",
        reasoned: true,
        required: true,
        enumValues: "",
        description: "",
        inSlim: true,
        inComplete: true,
        isNew: true,
      },
    ]);

  const onSave = async () => {
    const payload = toPayload(rows);
    const keys = payload.fields.map((f) => f.key);
    if (keys.some((k) => !k)) {
      toast("Every field needs a key", true);
      return;
    }
    if (new Set(keys).size !== keys.length) {
      toast("Duplicate field keys are not allowed", true);
      return;
    }
    setSaving(true);
    try {
      await save(payload, note || "ui-save");
      setNote("");
      toast("PDR schema saved");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Save failed", true);
    } finally {
      setSaving(false);
    }
  };

  const onRollback = async (versionId: string) => {
    setSaving(true);
    try {
      await rollback(versionId);
      toast("Rolled back");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Rollback failed", true);
    } finally {
      setSaving(false);
    }
  };

  const visible = rows
    .map((r, i) => ({ r, i }))
    .filter(
      ({ r }) =>
        !filter ||
        r.key.toLowerCase().includes(filter.toLowerCase()) ||
        r.label.toLowerCase().includes(filter.toLowerCase()),
    );

  return (
    <div className="flex flex-col h-full min-h-0">
      <PageTitle
        title="PDR Schema"
        meta={`${rows.length} fields · edits apply to future synthesis runs · every column is guaranteed to return`}
        actions={
          <>
            <Button variant="secondary" onClick={addField} disabled={saving}>
              <Icon name="add" /> Add field
            </Button>
            <Button variant="primary" onClick={onSave} disabled={saving || !dirty}>
              {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </Button>
          </>
        }
      />

      {error ? (
        <div className="px-6 py-2 text-body-sm text-error border-b border-error/30">
          {error} — showing bundled defaults.
        </div>
      ) : null}

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-6 py-4">
        {loading ? (
          <p className="text-body-sm text-on-surface-variant">Loading schema…</p>
        ) : (
          <div className="flex flex-col gap-4 max-w-5xl">
            <div className="flex items-center gap-3">
              <Input
                placeholder="Filter fields by key or label…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="max-w-xs"
              />
              <Input
                placeholder="Change note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="max-w-xs"
              />
            </div>

            {visible.map(({ r, i }) => (
              <div
                key={`${r.key || "new"}-${i}`}
                className="panel-border rounded p-4 flex flex-col gap-3 bg-surface-container-lowest"
              >
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      className="text-on-surface-variant hover:text-primary disabled:opacity-30"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      title="Move up"
                    >
                      <Icon name="keyboard_arrow_up" />
                    </button>
                    <button
                      type="button"
                      className="text-on-surface-variant hover:text-primary disabled:opacity-30"
                      onClick={() => move(i, 1)}
                      disabled={i === rows.length - 1}
                      title="Move down"
                    >
                      <Icon name="keyboard_arrow_down" />
                    </button>
                  </div>
                  {r.isNew ? (
                    <Input
                      placeholder="field_key (snake_case)"
                      value={r.key}
                      onChange={(e) => patch(i, { key: e.target.value })}
                      className="max-w-[16rem]"
                    />
                  ) : (
                    <span
                      className="font-data-mono text-body-sm text-on-surface px-2 py-1 rounded bg-surface-container"
                      title="Field keys are immutable (renaming would orphan stored data)"
                    >
                      {r.key}
                    </span>
                  )}
                  <div className="ml-auto">
                    <Button variant="danger" size="sm" onClick={() => remove(i)}>
                      <Icon name="delete" /> Remove
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Label">
                    <Input value={r.label} onChange={(e) => patch(i, { label: e.target.value })} />
                  </Field>
                  <Field label="Type">
                    <Select value={r.type} onChange={(e) => patch(i, { type: e.target.value })}>
                      {types.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {r.type === "enum" ? (
                    <Field label="Allowed values (comma-separated)">
                      <Input
                        value={r.enumValues}
                        onChange={(e) => patch(i, { enumValues: e.target.value })}
                        placeholder="Low, Medium, High"
                      />
                    </Field>
                  ) : (
                    <div />
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <Check
                    checked={r.reasoned}
                    onChange={(v) => patch(i, { reasoned: v })}
                    label="Reasoned (value + rationale)"
                  />
                  <Check
                    checked={r.required}
                    onChange={(v) => patch(i, { required: v })}
                    label="Required (guaranteed to return)"
                  />
                  <span className="w-px h-4 bg-outline-variant" />
                  <Check checked={r.inSlim} onChange={(v) => patch(i, { inSlim: v })} label="Slim" />
                  <Check
                    checked={r.inComplete}
                    onChange={(v) => patch(i, { inComplete: v })}
                    label="Complete"
                  />
                </div>

                <Field label="Extraction guidance (shown to the LLM)">
                  <Textarea
                    value={r.description}
                    onChange={(e) => patch(i, { description: e.target.value })}
                    placeholder="Optional: how the model should populate this field."
                    rows={2}
                  />
                </Field>
              </div>
            ))}

            {versions.length > 0 ? (
              <div className="panel-border rounded p-4 mt-4">
                <h3 className="font-data-label text-data-label uppercase text-on-surface-variant mb-2">
                  Version history
                </h3>
                <ul className="flex flex-col gap-2">
                  {versions.map((v) => (
                    <li key={v.id} className="flex items-center gap-3 text-body-sm">
                      <span className="font-data-mono text-on-surface-variant">{v.id}</span>
                      <span className="text-on-surface truncate flex-1">
                        {v.note || "(no note)"} · {v.field_count} fields
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRollback(v.id)}
                        disabled={saving}
                      >
                        Roll back
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
